<script setup lang="ts">
	import {
		ArcElement,
		Chart as ChartJS,
		Legend,
		Title,
		Tooltip,
	} from 'chart.js'
	import ChartDataLabels from 'chartjs-plugin-datalabels'
	import { computed } from 'vue'
	import { Doughnut } from 'vue-chartjs'

	import useCompanyReportHotelAnalytics from '@/modules/companies/composables/useCompanyReportHotelAnalytics'

	interface Props {
		companyId?: number
		dateRange?: string[] | null
		creationDateRange?: string[] | null
		statusFilter?: string
		groupByFilter?: string
		searchTravelerId?: number | null
		searchHotelId?: number | null
		createdAt?: string[] | null
		orderBy?: string
		orderDirection?: 'asc' | 'desc'
	}

	const props = defineProps<Props>()

	const { analytics, isFetching: isLoading } = useCompanyReportHotelAnalytics(
		computed(() => ({
			companyId: props.companyId,
			filters: {
				dates: props.dateRange,
				creationDates: props.creationDateRange,
				status: props.statusFilter !== 'all' ? props.statusFilter : undefined,
				groupBy: props.groupByFilter,
				travelerId: props.searchTravelerId || undefined,
				hotelId: props.searchHotelId || undefined,
				createdAt: props.createdAt || undefined,
				orderBy: props.orderBy || undefined,
				orderDirection: props.orderDirection || undefined,
			},
		}))
	)

	ChartJS.register(Title, Tooltip, Legend, ArcElement, ChartDataLabels)

	const chartData = computed(() => {
		if (
			!analytics.value?.dailyOccupancy ||
			analytics.value.dailyOccupancy.length === 0
		)
			return null

		// Verificar que al menos un día tenga reservaciones
		const hasReservations = analytics.value.dailyOccupancy.some(
			(i) => i.reservations > 0
		)
		if (!hasReservations) return null

		return {
			labels: analytics.value.dailyOccupancy.map((i) => i.day),
			datasets: [
				{
					label: 'Reservaciones por día',
					data: analytics.value.dailyOccupancy.map(
						(i) => i.percentageOfTotalReservations
					),
					backgroundColor: [
						'rgba(255, 99, 132, 0.8)',
						'rgba(54, 162, 235, 0.8)',
						'rgba(255, 206, 86, 0.8)',
						'rgba(75, 192, 192, 0.8)',
						'rgba(153, 102, 255, 0.8)',
						'rgba(255, 159, 64, 0.8)',
						'rgba(199, 199, 199, 0.8)',
					],
					borderColor: [
						'rgba(255, 99, 132, 1)',
						'rgba(54, 162, 235, 1)',
						'rgba(255, 206, 86, 1)',
						'rgba(75, 192, 192, 1)',
						'rgba(153, 102, 255, 1)',
						'rgba(255, 159, 64, 1)',
						'rgba(199, 199, 199, 1)',
					],
					borderWidth: 1,
				},
			],
		}
	})

	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				position: 'right' as const,
			},
			title: {
				display: true,
				text: 'Días más reservados',
			},
			tooltip: {
				callbacks: {
					label: (context: any) => {
						const value = context.raw
						const dayData = analytics.value?.dailyOccupancy?.[context.dataIndex]
						if (dayData) {
							return [
								`Porcentaje: ${value}%`,
								`Reservaciones: ${dayData.reservations}`,
								`Total noches: ${dayData.totalNights}`,
								`Gasto total: $${dayData.totalSpend.toLocaleString()} MXN`,
							]
						}
						return `${value}%`
					},
				},
			},
			datalabels: {
				display: true,
				color: '#fff',
				font: {
					weight: 'bold' as const,
					size: 12,
				},
				formatter: (value: number) => {
					if (value === 0) return ''
					return `${value}%`
				},
			},
		},
	}

	// Exponer isLoading para el componente padre
	defineExpose({
		isLoading,
	})
</script>

<template>
	<div class="overflow-hidden rounded-lg bg-white p-6 shadow">
		<div
			v-if="isLoading && !chartData"
			class="flex items-center justify-center"
			style="height: 300px"
		>
			<div class="text-center">
				<span
					class="material-symbols-outlined mb-2 animate-spin text-4xl text-blue-ef-500"
				>
					progress_activity
				</span>
				<p class="text-sm text-gray-600">Cargando ocupación...</p>
			</div>
		</div>
		<div
			v-else-if="chartData"
			style="height: 300px"
		>
			<Doughnut
				:data="chartData"
				:options="chartOptions"
			/>
		</div>
		<div
			v-else
			class="flex items-center justify-center h-[300px]"
		>
			<div class="text-center">
				<span class="material-symbols-outlined mb-4 text-6xl text-gray-300">
					calendar_month
				</span>
				<p class="text-base font-medium text-gray-600">
					No hay datos disponibles para esta sección
				</p>
				<p class="mt-2 text-sm text-gray-500">
					No se encontró ocupación por día de la semana
				</p>
			</div>
		</div>
	</div>
</template>
